import CalculateOutlinedIcon from "@mui/icons-material/CalculateOutlined";
import DirectionsBoatOutlinedIcon from "@mui/icons-material/DirectionsBoatOutlined";
import FactoryOutlinedIcon from "@mui/icons-material/FactoryOutlined";
import Inventory2OutlinedIcon from "@mui/icons-material/Inventory2Outlined";
import PlaceOutlinedIcon from "@mui/icons-material/PlaceOutlined";

import type { ServicePage } from "../types";

/**
 * 主資料的子頁宣告。**這是唯一要維護的地方**（見 `../types.ts`）。
 *
 * 五個面向對應乾料庫存管理需要的共用主資料表：廠區、料號（含料號
 * 與產地兩張各自獨立的平面清單）、運船、地點、係數設定（砂石車換算
 * 係數）。
 *
 * 「料號」這個面向底下其實是兩張互不相關的表，不是一層或兩層的樹狀
 * 結構：料號本身是什麼東西，跟這批貨從哪裡來（產地）是兩件事——
 * 同一個產地可以對應不只一種料號，也有些料號根本沒有產地這個維度。
 * 兩張表用頁內 tab 切換（`MaterialCategoryPage` 底下的
 * `MaterialCategoryList` / `MaterialOriginList`），理由寫在該檔案開頭。
 *
 * 「運船」與「地點」原本是同一張「運輸來源」表（更早叫「廠商」／
 * Carrier）、用運輸方式區分，後來拆成 `Vessel`／`LandLocation` 兩張表
 * （理由見 services/master/models.py 的 Vessel 說明）。拆開之後各自是一個
 * 面向、跟廠區同一層，不再包一層「運輸來源」用頁內 tab 切換——使用者要
 * 找的是運船或地點本身，多一層只是多點一下（見 `VesselListPage` 開頭）。
 *
 * 「係數設定」（砂石車換算係數）單獨成一個面向，不是掛在運船或地點底下：
 * 砂石車能裝多少噸是砂石車本身的屬性，跟貨是從哪艘運船、哪個地點、哪種
 * 方式來的無關，混在那些清單裡會讓人誤以為兩者有關聯（見
 * `TruckCapacityPage` 開頭的說明）。這張表固定只有一筆，畫面上不開放
 * 新增或刪除。
 *
 * 除了「係數設定」，其餘表都不讓使用者自己輸入代碼——資料庫的 id 就
 * 夠用了，使用者實際操作時看不到、也用不到代碼。
 *
 * 每個面向都有自己的路徑（`plant` / `materials` / `vessels` /
 * `land-locations` / `truck-capacity`），沒有一個借用服務根路徑：`/master` 本身不是任何
 * 一個面向，`buildServiceRoutes` 會自動轉址到這裡宣告的第一個面向
 * （廠區）。這樣所有面向的網址規則完全一致，都可以被加進書籤或分享
 * 連結。
 */
export const masterPages: ServicePage[] = [
  {
    path: "plant",
    label: "廠區",
    title: "廠區",
    icon: FactoryOutlinedIcon,
    permissions: ["master.view_plant"],
    load: async () => ({ Component: (await import("./PlantListPage")).PlantListPage }),
  },
  {
    path: "materials",
    label: "料號",
    title: "料號",
    icon: Inventory2OutlinedIcon,
    permissions: ["master.view_materialcategory"],
    load: async () => ({
      Component: (await import("./MaterialCategoryPage")).MaterialCategoryPage,
    }),
  },
  {
    path: "vessels",
    label: "運船",
    title: "運船",
    icon: DirectionsBoatOutlinedIcon,
    permissions: ["master.view_vessel"],
    load: async () => ({
      Component: (await import("./VesselListPage")).VesselListPage,
    }),
  },
  {
    path: "land-locations",
    label: "地點",
    title: "地點",
    icon: PlaceOutlinedIcon,
    permissions: ["master.view_landlocation"],
    load: async () => ({
      Component: (await import("./LandLocationListPage")).LandLocationListPage,
    }),
  },
  {
    path: "truck-capacity",
    label: "係數設定",
    title: "係數設定",
    icon: CalculateOutlinedIcon,
    permissions: ["master.view_truckcapacityrate"],
    load: async () => ({
      Component: (await import("./TruckCapacityPage")).TruckCapacityPage,
    }),
  },
];
